import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PageHeader, StatCard, DataTable } from "@/components/admin/ui";
import { useFetch } from "@/hooks/useFetch";
import { formatDate } from "@/lib/api";
import { PAYMENT_STATE } from "@/lib/status";

function Warnings({ data }) {
  const items = [];
  if (!data.payments_enabled) items.push(["Razorpay keys are not configured yet. Parents cannot pay online; use 'Mark as paid' for offline payments.", "/admin/settings"]);
  if (!data.r2_enabled) items.push(["Cloudflare R2 keys are not configured yet. Report PDFs are stored in Emergent cloud storage until R2 keys are added.", "/admin/reports"]);
  if (data.failed_emails) items.push([`${data.failed_emails} email(s) failed to send.`, "/admin/emails"]);
  return (
    <div className="space-y-2 mb-8">
      {items.map(([text, to]) => (
        <Link key={text} to={to} className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 hover:bg-amber-100" data-testid="admin-warning">
          <AlertTriangle className="h-4 w-4 shrink-0" /><span className="flex-1">{text}</span><ArrowRight className="h-4 w-4" />
        </Link>
      ))}
    </div>
  );
}

export default function Overview() {
  const { data } = useFetch("/admin/overview");
  if (!data) return <PageHeader title="Overview" />;
  const classData = Object.entries(data.students_by_class).map(([k, v]) => ({ name: `Class ${k}`, students: v }));
  return (
    <div data-testid="admin-overview">
      <PageHeader eyebrow="Admin console" title="Overview" description="A snapshot of families, consent, payments and system health." />
      <Warnings data={data} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Parents" value={data.parents} hint={`${data.verified_parents} verified`} testId="stat-parents" />
        <StatCard label="Students" value={data.students} testId="stat-students" />
        <StatCard label="Active consents" value={data.consents} testId="stat-consents" />
        <StatCard label="Revenue" value={`Rs. ${data.revenue_inr}`} hint={`${data.paid} paid assessments`} testId="stat-revenue" />
      </div>
      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-surface p-6">
          <h2 className="font-display font-bold">Students by class</h2>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={classData}>
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#635060" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#635060" }} axisLine={false} tickLine={false} width={30} />
                <Tooltip cursor={{ fill: "#F3E8F1" }} />
                <Bar dataKey="students" fill="#4F0341" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card-surface p-6">
          <h2 className="font-display font-bold">Payment states</h2>
          <ul className="mt-4 space-y-3">
            {Object.keys(PAYMENT_STATE).map((k) => (
              <li key={k} className="flex justify-between text-sm"><span className="text-[#635060]">{PAYMENT_STATE[k].label}</span><span className="font-mono font-semibold">{data.payment_states[k] || 0}</span></li>
            ))}
          </ul>
          <p className="mt-6 text-sm"><Link to="/admin/messages" className="text-[#4F0341] font-semibold link-underline">{data.new_messages} new contact message(s)</Link></p>
        </div>
      </div>
      <h2 className="mt-10 mb-3 font-display text-lg font-bold">Recent parents</h2>
      <DataTable testId="recent-parents-table" rows={data.recent_parents} columns={[
        { key: "name", label: "Name" }, { key: "email", label: "Email" },
        { key: "source", label: "Source", render: (r) => (r.source === "admin_provisioned" ? "Admin" : "Self sign-up") },
        { key: "email_verified", label: "Verified", render: (r) => (r.email_verified ? "Yes" : "No") },
        { key: "created_at", label: "Joined", render: (r) => formatDate(r.created_at) },
      ]} />
    </div>
  );
}
